import { GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { ProductType, ProductTypeTag, ProductTypeTagMap } from "app/model/entities/entity-model";
import { AgFns, GridOptionsExt, ISortModel, RowSelectionMultipleCheckbox } from "app/shared/ag-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { EntityFinderDialog } from "app/shared/entity-finder.dialog";
import { takeUntil } from "rxjs/operators";
import { EditGenericEntityDialogComponent, ProductTypeTagDialogParams } from "../shared/edit-generic-entity-dialog.component";
import { ProductTypeFinderDialogComponent } from "./product-type-finder-dialog.component";
import { UniAgFns } from "@/shared/uni-ag-fns";

@Component({
  selector: "app-product-type-tag",
  templateUrl: "./product-type-tag.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ProductTypeTagComponent extends DomainBaseComponent implements OnInit {
  ptTagGridOptions: GridOptionsExt;
  productTypeTags: ProductTypeTag[] = [];
  selectedProductTypeTag: ProductTypeTag;

  ptTagMapGridOptions: GridOptionsExt;
  productTypeTagMaps: ProductTypeTagMap[];
  // DialogBaseComponent: any;

  constructor(protected domainService: DomainService, protected route: ActivatedRoute) {
    super(domainService);
    this.user = domainService.authService.getUser();
    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.ptTagGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPtTagGridReady,
      onRowSelected: this.onPtTagRowSelected,
      accentedSort: true,
    });

    // AgFns.captureGridRouteParams(this.ptTagGridOptions, this.route, "id");
    this.captureLocation(this.ptTagGridOptions, "id");

    this.ptTagMapGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPtTagMapGridReady,
      getRowId: (p) => p.data.productTypeId.toString(),
    });

    this.productTypeTags = await this.dbQueryService.getProductTypeTags();

    this.isPageReady = true;
  }

  async canDeactivate() {
    const ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(ProductTypeTag);
    this.uow.clearEntities(ProductType);
    return true;
  }

  async onPtTagGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: "Name", field: "name", filter: "agTextColumnFilter" },
      {
        ...AgFns.createButtonProps("", this.editProductTypeTag.bind(this), {
          label: "Edit",
        }),
      },
      {
        ...AgFns.createButtonProps("", this.deleteProductTypeTag.bind(this), {
          label: "Delete",
        }),
      },
      // { headerName: 'Id', field: 'id', filter: 'agNumberColumnFilter' },
    ];
    const sortModel = [{ colId: "name", sort: "asc" }] as ISortModel;
    AgFns.initGrid(event, colDefs, sortModel);

    await this.applyLocation();
  }

  async onPtTagMapGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createButtonProps("", this.detachProductType.bind(this), {
          label: "Detach",
        }),
        ...AgFns.fixWidth(100),
      },

      { ...UniAgFns.getManufacturerColDef("productType.manufacturer.name") },
      { ...UniAgFns.getProductTypeSKUColDef("productType.style") },
      { ...UniAgFns.getProductTypeDescriptionColDef("productType.description") },

      {
        ...AgFns.createButtonProps("", this.editProductType.bind(this), {
          label: "View Product Type",
        }),
        ...AgFns.fixWidth(140),
      },

      { ...UniAgFns.getActiveStatusColDef("productType.activeStatus.name") },
      // {
      //   headerName: "Id",
      //   field: "productTypeId",
      //   filter: "agNumberColumnFilter",
      // },
    ];

    const sortModel = [
      { colId: "productType.manufacturer.name", sort: "asc" },
      { colId: "productType.description", sort: "asc" },
      { colId: "productType.style", sort: "asc" },
    ] as ISortModel;

    AgFns.initGrid(event, colDefs, sortModel);
  }

  async onPtTagRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) {
      return;
    }
    const ptTag = event.data as ProductTypeTag;
    if (ptTag == null) {
      return;
    }
    this.replaceLocation(ptTag.id);
    this.selectedProductTypeTag = ptTag;
    this.productTypeTagMaps = await this.dbQueryService.getProductTypeMapsForTag(ptTag.id);
  }

  async editProductTypeTag(ptTag: ProductTypeTag) {
    const r = await DialogBaseComponent.open(EditGenericEntityDialogComponent, this, {
      ...ProductTypeTagDialogParams,
      target: ptTag,
      dialogWidth: "20vw",
      dialogHeight: "25vh",
    });
    if (r) {
      AgFns.refreshGrid(this.ptTagGridOptions, this.productTypeTags);
      this.toastr.success("Product Type Tag updated");
    }
  }

  async addProductTypeTag() {
    const r = await DialogBaseComponent.open(EditGenericEntityDialogComponent, this, {
      ...ProductTypeTagDialogParams,
      target: null,
      dialogWidth: "20vw",
      dialogHeight: "25vh",
    });
    if (r) {
      this.productTypeTags = await this.dbQueryService.getProductTypeTags();
      AgFns.refreshGrid(this.ptTagGridOptions, this.productTypeTags);
      this.toastr.success("Product Type Tag added");
    }
  }

  async deleteProductTypeTag(ptTag: ProductTypeTag) {
    // Just insurance before we try to delete
    this.productTypeTagMaps = await this.dbQueryService.getProductTypeMapsForTag(ptTag.id);
    if (ptTag.productTypeTagMaps.length > 0) {
      this.dialogService.okDialog("Cannot delete Product Type Tag", "This tag is attached to one or more product types.");
      return;
    }
    ptTag.entityAspect.setDeleted();
    await this.dbSaveService.saveSelectedChanges([ptTag]);
    this.productTypeTags = await this.dbQueryService.getProductTypeTags();
    AgFns.refreshGrid(this.ptTagGridOptions, this.productTypeTags);
    this.toastr.success("Product Type Tag deleted");
  }

  async attachProductTypes() {
    const r = await EntityFinderDialog.open(ProductTypeFinderDialogComponent, this, { rowSelection: RowSelectionMultipleCheckbox });
    if (r) {
      const istIdSet = new Set(this.productTypeTagMaps.map((x) => x.productTypeId));
      var newIsts = r.filter((x) => !istIdSet.has(x.id));
      var newMaps = newIsts.map((x) => {
        return this.uow.createEntity(ProductTypeTagMap, {
          productTypeId: x.id,
          productTypeTagId: this.selectedProductTypeTag.id,
        });
      });
      await this.dbSaveService.saveSelectedChanges(newMaps);
      this.productTypeTagMaps = await this.dbQueryService.getProductTypeMapsForTag(this.selectedProductTypeTag.id);
      AgFns.refreshGrid(this.ptTagMapGridOptions, this.productTypeTagMaps);
      this.toastr.success("Product Type Tag successfully attached");
    }
  }

  async detachProductType(ptTagMap: ProductTypeTagMap) {
    this.productTypeTagMaps = this.productTypeTagMaps.filter((x) => x.productTypeId != ptTagMap.productTypeId);
    ptTagMap.entityAspect.setDeleted();
    await this.dbSaveService.saveSelectedChanges([ptTagMap]);
    AgFns.refreshGrid(this.ptTagMapGridOptions, this.productTypeTagMaps);
    this.toastr.success("Product Type Tag successfully detached");
  }

  editProductType(ptTagMap: ProductTypeTagMap) {
    this.router.navigate(["/references/manage/product-type", ptTagMap.productTypeId]);
    return;
  }
}
